/**
 * record-calendar.js
 */

import React from 'react';
import { flushSync } from 'react-dom';
import { Box, Badge, Fade, IconButton, Tooltip, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { styled } from '@mui/material/styles';
import { StaticDatePicker, PickersDay } from '@mui/x-date-pickers';
import { useRouteMatch } from 'react-router-dom';
import { format, addMonths, startOfMonth, parseISO, parse } from 'date-fns';
import { Types } from 'utils/constants';
import { dateParamFormat, calMonthFormat } from 'utils/datetime';
import { getFilterParamsObj, getFilterParamsString } from 'utils/filter-params';
import usePrevious from 'utils/use-previous';
import useRecordStore from 'contexts/record-store';
import useRecordList from 'contexts/record-list';
import RouteLink from 'routing/route-link';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(.5),
    marginLeft: theme.spacing(-.5),
    marginRight: theme.spacing(-.5),
    [theme.breakpoints.up('750')]: {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingBottom: theme.spacing(2),
      marginLeft: 0,
      marginRight: 0,
    },
  },
  transitionContainer: {
    flexGrow: 1,
    position: 'relative',
    display: 'flex',
  },
  navButtons: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    right: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  trainingCalendar: {
    position: 'relative',
    '& .MuiPickersLayout-root': {
      minWidth: 'auto',
    },   
    '& .MuiDateCalendar-root': {
      width: '100%',
      height: 'unset',
      maxHeight: 'unset',
    },
    '& .MuiPickersCalendarHeader-root': {
      padding: 0,
      margin: `${theme.spacing(0.5)} ${theme.spacing(0.25)} ${theme.spacing(1.5)}`,
      justifyContent: 'center',
      minHeight: 23,
      '& .MuiPickersArrowSwitcher-root': {
        display: 'none',
      },
      '& .MuiPickersCalendarHeader-labelContainer': {
        margin: 0,
      },     
    },
    '& .MuiPickersSlideTransition-root': {
      minHeight: 'unset',
      '& > *': {
        position: 'unset',
      },
    },
    '& .MuiDayCalendar-weekDayLabel': {
      width: 'auto',
      height: 'auto',
      flex: 1,
      color: theme.palette.sys.onSurfaceVariant,
    },
    '& .MuiDayCalendar-weekContainer': {
      margin: 0,
      '& > div': {
        flex: 1,
        margin: 3,
      }
    },
    '& .MuiPickersDay-root': {
      fontSize: theme.typography.caption.fontSize,
      width: '100%',
      height: 32,
      flexDirection: 'column',
      justifyContent: 'start',
      borderRadius: 4,
      margin: 0,
      transition: `100ms ${theme.transitions.easing.easeOut}`,
      backgroundColor: theme.palette.comp.calendar.dayBackground,
      '&:hover': {
        backgroundColor: theme.palette.action.hoverBackground(theme.palette.comp.calendar.dayBackground),
        textDecoration: 'none'
      },
      [theme.breakpoints.down('lg')]: {
        fontSize: theme.typography.button.fontSize,
        height: 40,
      },
    },
    '& .MuiDayCalendar-weekContainer .MuiPickersDay-root.Mui-selected': {
      color: 'inherit',
      backgroundColor: theme.palette.comp.calendar.dayHighlightBackground,
      border: `1px solid ${theme.palette.sys.outline}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hoverBackground(theme.palette.comp.calendar.dayHighlightBackground)
      }
    },
    '& .MuiPickersDay-root.MuiPickersDay-today, & .MuiPickersDay-root.MuiPickersDay-today.Mui-selected': {
      color: 'white',
      background: `linear-gradient(${theme.palette.sys.primary} 50%, ${theme.palette.comp.calendar.dayBackground} 50%)`,
      borderColor: theme.palette.sys.primary,
    },
  },
  badgeIcon: {
    '&.undecided': {
      color: theme.palette.secondary.colors[5],
    },
    '&.not-attending': {
      color: theme.palette.grey.colors[6],
      opacity: 0.5,
    },
    '&.event': {
      color: theme.palette.sys.primary,
    },
    '&.deployment': {
      color: theme.palette.grey.colors[3]
    },
    '&.trainingClass': {
      color: theme.palette.comp.calendar.trainingClassBadge,   
    },
    '&.vetVisit': {
      color: theme.palette.comp.calendar.vetVisitBadge,
    },
  }
}));


const CalBadge = styled(Badge, {
  shouldForwardProp: (prop) => prop !== 'squeezed',
})(({ squeezed, theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  '& .MuiBadge-badge': {
    display: 'flex',
    pointerEvents: 'none',
    top: 'unset',
    right: 'unset',
    transform: 'unset',
    transformOrigin: 'unset',
    justifyContent: 'space-evenly',
    padding: '4px 0px 0px',
    width: '90%',
    minWidth: 32,
    '& > svg': {
      fontSize: squeezed ? 9 : 12,
      margin: '-1px',
    }
  }
}));


const CalDay = React.memo(function(props) {
  const {
    day,
    outsideCurrentMonth,
    handlers
  } = props;

  const classes = useStyles();
  const recordStore = useRecordStore();
  const dateItem = recordStore.useDate(day).ornull;
  const dateRecords = dateItem && dateItem.records.get();
  const dateSelected = dateItem && dateItem.selected.get();
  

  if (outsideCurrentMonth) {
    return <div/>;
  }


  const getBadge = ({ record, multiple }) => {
    const undecidedTraining = record.recordType === Types.Record.EVENT
      && record.membership?.isOptional
      && record.membership?.isAttending === null;
    const notAttendingTraining = record.recordType === Types.Record.EVENT
      && record.membership?.isOptional
      && record.membership?.isAttending === false;
    const fill = (undecidedTraining 
      || notAttendingTraining 
      || record.recordType === Types.Record.VET_VISIT 
      || record.isComplete
    ) ? "filled" : "outlined";
    const shape = record.recordType === Types.Record.EVENT 
      ? "circle"
      : record.recordType === Types.Record.DEPLOYMENT
        ? "star"
        : record.recordType === Types.Record.CLASS
          ? "square"
          : "plus";
    const className = undecidedTraining
      ? 'undecided'
      : notAttendingTraining
        ? 'not-attending'
        : record.recordType === Types.Record.EVENT
          ? 'event'
          : record.recordType === Types.Record.DEPLOYMENT
            ? 'deployment'
            : record.recordType === Types.Record.CLASS
              ? 'trainingClass'
              : 'vetVisit'


    return <Icon
      type={`${fill}-${shape}${multiple ? '-multiple': ''}`}
      className={`${classes.badgeIcon} ${className}`}
    />;
  }


  const getBadgeRecords = (dateRecords) => {
    const result = {
      deployment: null,
      event: null,
      trainingClass: null,
      vetVisit: null,
    };

    if (!dateRecords || dateRecords.length === 0) {
      return result;
    }
  
    const deployments = dateRecords.filter(rec => rec.recordType === Types.Record.DEPLOYMENT);
    const events = dateRecords.filter(rec => rec.recordType === Types.Record.EVENT);
    const trainingClasses = dateRecords.filter(rec => rec.recordType === Types.Record.CLASS);
    const vetVisits = dateRecords.filter(rec => rec.recordType === Types.Record.VET_VISIT);

    if (deployments.length) {
      const incomplete = deployments.find(rec => !rec.isComplete);
      result.deployment = {
        record: incomplete ? incomplete : deployments[0],
        multiple: deployments.length > 1
      }
    }

    if (events.length) {
      const incomplete = events.find(rec => !rec.isComplete);
      result.event = {
        record: incomplete ? incomplete : events[0],
        multiple: events.length > 1
      }
    }
    
    if (trainingClasses.length) {
      const incomplete = trainingClasses.find(rec => !rec.isComplete);
      result.trainingClass = {
        record: incomplete ? incomplete : trainingClasses[0],
        multiple: trainingClasses.length > 1
      }
    }

    if (vetVisits.length) {
      const incomplete = vetVisits.find(rec => !rec.isComplete);
      result.vetVisit = {
        record: incomplete ? incomplete : vetVisits[0],
        multiple: vetVisits.length > 1
      }
    }
        
    return result;
  }

  const dayFilterParams = {
    date: format(day, dateParamFormat)
  };
  if (handlers) {
    dayFilterParams.handlers = handlers;
  }

  const dayComponent = <PickersDay
    {...props}
    selected={dateSelected}
    onClick={e => {
      e.stopPropagation();
    }}
    component={RouteLink}
    pathSectionName='records'
    pathItemName={'params'}
    pathParams={{ 
      p1: Types.FilterMode.DATE,
      filterParams: getFilterParamsString(dayFilterParams)
    }}
  />

  let component = dayComponent;

  if (dateRecords && dateRecords.length !== 0) {
    const badgeRecs = getBadgeRecords(dateRecords);
    const componentBadge =
      <CalBadge
        badgeContent={<>
          {badgeRecs.event && getBadge(badgeRecs.event)}
          {badgeRecs.deployment && getBadge(badgeRecs.deployment)}
          {badgeRecs.trainingClass && getBadge(badgeRecs.trainingClass)}          
          {badgeRecs.vetVisit && getBadge(badgeRecs.vetVisit)}          
        </>}
        squeezed={badgeRecs.event && badgeRecs.deployment && badgeRecs.trainingClass && badgeRecs.vetVisit}
      >
        {dayComponent}
      </CalBadge>;

    if (dateRecords.length <= 1) {
      component = componentBadge;
    } else {
      // build counts for tooltip
      let numEvents = 0;
      let numDeploys = 0;
      let numClasses = 0;
      let numVetVisits = 0;
      dateRecords.forEach(record => {
        if (record.recordType === Types.Record.EVENT) {
          numEvents++;
        } else if (record.recordType === Types.Record.DEPLOYMENT) {
          numDeploys++;
        } else if (record.recordType === Types.Record.CLASS) {
          numClasses++;
        } else if (record.recordType === Types.Record.VET_VISIT) {
          numVetVisits++;
        }       
      });

      component = (
        <Tooltip
          arrow
          title={<>
            {numEvents > 0 && <Box>{numEvents} Training Event{numEvents > 1 ? 's' : ''}</Box>}
            {numDeploys > 0 && <Box>{numDeploys} Deployment{numDeploys > 1 ? 's' : ''}</Box>}
            {numClasses > 0 && <Box>{numClasses} Class{numClasses > 1 ? 'es' : ''}</Box>}            
            {numVetVisits > 0 && <Box>{numVetVisits} Vet Visit{numVetVisits > 1 ? 's' : ''}</Box>}            
          </>}
        >
          {componentBadge}
        </Tooltip>
      );
    }
  }

  return <div>{component}</div>;

}, (prev, next) => {
  return (
    prev.date === next.date
    && JSON.stringify(prev.handlers || '') === JSON.stringify(next.handlers || '')
  )
});


const CalMonth = function(props) {
  const {
    month,
    handlers,
  } = props;
  const classes = useStyles();

  return (
    <Box
      key={month}
      flex={1}
      className={classes.trainingCalendar}
      data-testtype='records-calendar-month'
    >
      <StaticDatePicker
        views={['day']}
        value={month}
        dayOfWeekFormatter={(weekday) => `${format(weekday, 'EEEEEE')}`}
        slots={{
          day: CalDay,
          actionBar: null,
        }}
        slotProps={{
          day: {
            handlers: handlers,
          },
          toolbar: { hidden: true },
          actionBar: { actions: [] },
        }}
      />
    </Box>
  )
}


export default function RecordCalendar(props) {
  const {
    maxCalendars,
  } = props;
  const classes = useStyles();

  const { params } = useRouteMatch();
  const filterParamsString = params.filterParamsString;
  const filterParams = getFilterParamsObj(filterParamsString);
  const calendarDate = filterParams.date || null;
  const handlersString = JSON.stringify(filterParams.handlers || '');
  const prevHandlersString = usePrevious(handlersString);

  const recordStore = useRecordStore();
  const calendarStatus = recordStore.useCalendarStatus();
  const isCalendarLoading = calendarStatus.isLoading.get();
  const calendarChunkStartDate = calendarStatus.chunkStartDate?.get();

  const recordList = useRecordList();
  const records = recordList.useState().records;
  const numRecords = records.length;
  const firstRecordDate = numRecords > 0 ? records[0].date : null;

  const isScreenMedWidth = useMediaQuery(theme => theme.breakpoints.up("sm_md"), { noSsr: true });
  const isScreenLrgWidth = useMediaQuery(theme => theme.breakpoints.up("lg"), { noSsr: true });
  const numCalendars = Math.min(isScreenLrgWidth ? 3 : isScreenMedWidth ? 2 : 1, maxCalendars);
  const [firstMonth, setFirstMonth] = React.useState('');
  const prevFirstMonth = usePrevious(firstMonth);
  const [calendars, setCalendars] = React.useState([]);
  const [calHeight, setCalHeight] = React.useState(0);
  const [translate, setTranslate] = React.useState(-100/(numCalendars + 2));
  const [isCalendarLoadingVisibleMonth, setIsCalendarLoadingVisibleMonth] = React.useState(false);

  const transitionDuration = 300;
  const transitionString = `${transitionDuration}ms ease-in-out`;
  const [transition, setTransition] = React.useState(transitionString);


  // sets the primary month
  const setPrimaryMonth = React.useCallback((date) => {
    const newFirstMonth = addMonths(startOfMonth(date), -numCalendars + 1);
    setFirstMonth(format(newFirstMonth, calMonthFormat));
    setTranslate(-100/(numCalendars + 2));
  }, [numCalendars]);


  // set initial date
  React.useEffect(() => {
    if (!firstMonth) {
      if (calendarDate) {
        setPrimaryMonth(parseISO(calendarDate));
      } else {
        setPrimaryMonth(new Date());
      }
    }
  }, [firstMonth, calendarDate, setPrimaryMonth])


  // reset first cal date when handlers changes
  React.useEffect(() => {
    if (firstMonth && handlersString !== prevHandlersString) {
      setPrimaryMonth(new Date());
    }
  }, [firstMonth, handlersString, prevHandlersString, setPrimaryMonth]);
  

  // check for loading more calendar data
  React.useEffect(() => {
    if (firstMonth && calendarChunkStartDate) {
      const firstMonthDate = parse(firstMonth, calMonthFormat, new Date());
      const chunkStartDate = parseISO(calendarChunkStartDate);
      setIsCalendarLoadingVisibleMonth(firstMonthDate < chunkStartDate);
      if (addMonths(firstMonthDate, -1) < chunkStartDate) {
        setTimeout(() => recordStore.fetchNextCalendarChunk());
      }
    }
  // recordStore is hookstate class obj
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstMonth, calendarChunkStartDate])


  // shift calendar with record list pagination
  React.useEffect(() => {
    if (firstRecordDate && !calendarDate) {
      const firstRecordDateParsed = parseISO(firstRecordDate);
      if (firstRecordDateParsed <= Date.now()) {
        setTimeout(() => setPrimaryMonth(firstRecordDateParsed));
      } else {
        setTimeout(() => setPrimaryMonth(Date.now()));
      }
    }
  }, [firstRecordDate, calendarDate, setPrimaryMonth, numCalendars]);


  // generate calendars when firstMonth or handlers filter param changes
  React.useEffect(() => {
    if (!firstMonth) return;
    const handlersChanged = handlersString !== prevHandlersString;
    const firstMonthDate = parse(firstMonth, calMonthFormat, new Date());
    const calendarSpecs = [];

    // populate date state for new months and generate calendar specs
    // (NOTE: calling createDateStates inside the following setCalendars
    // loop triggers a somewhat misleading React warning about updating
    // state during a render.)
    for (let i = -1; i < numCalendars + 1; i++) {
      const calDate = addMonths(firstMonthDate, i);
      const calId = format(calDate, calMonthFormat);
      const cachedCal = calendars.find(cal => cal.id === calId);
      if (handlersChanged || !cachedCal) {
        recordStore.createDateStates(calDate);
      }
      calendarSpecs.push({id: calId, date: calDate});
    }

    // update calendars state
    setCalendars(prevCalendars => {
      let newCalendars = [];
      for (let calSpec of calendarSpecs) {
        const cachedCal = prevCalendars.find(cal => cal.id === calSpec.id);
        if (!handlersChanged && cachedCal) {
          newCalendars.push(cachedCal);
        } else {
          const handlers = handlersString ? JSON.parse(handlersString) : null;
          newCalendars.push({
            id: calSpec.id,
            date: calSpec.date,
            calendar:
              <CalMonth
                month={calSpec.date}
                handlers={handlers}
              />
          });
        }
      }

      return newCalendars;
    });
    setTranslate(-100 / (numCalendars + 2));
    
  // recordStore.createDateStates is setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numCalendars, firstMonth, prevFirstMonth, handlersString, prevHandlersString]);


  const getCalHeight = (i, calHeight, numCalendars) => {
    if (calHeight === 0) {
      return i === 0 || i === numCalendars + 1 ? 0 : 'auto';
    } else if (calHeight === -1) {
      return i >= numCalendars ? 0 : 'auto';
    } else if (calHeight === 1) {
      return i <= 1 ? 0 : 'auto';
    }
  }


  const navMonth = (direction) => {
    const newFirstMonth = addMonths(parse(firstMonth, calMonthFormat, new Date()), direction);

    if (calendarChunkStartDate) {
      const chunkStartDate = parseISO(calendarChunkStartDate);
      setIsCalendarLoadingVisibleMonth(newFirstMonth < chunkStartDate);
    }

    setCalHeight(direction);
    setTranslate(direction > 0
      ? (-100/(numCalendars + 2)) * 2
      : 0);
    setTimeout(() => {
      flushSync(() => {
        setTransition();
        setFirstMonth(p => format(addMonths(parse(p, calMonthFormat, new Date()), direction), calMonthFormat));
        setTranslate(-100/(numCalendars + 2));
        setCalHeight(0);
      });
      setTimeout(() => {
        setTransition(transitionString);
      });
    }, transitionDuration + 50);
  }


  if (!firstMonth) return null;

  return (
    <Fade in={true} timeout={500}>
      <FeatureBox fadeBackground loading={isCalendarLoading || isCalendarLoadingVisibleMonth}>
        <div className={classes.root} id='records-calendar' data-testid="records-calendar">

          {/* calendars */}
          <Box
            flex={1}
            display="flex"
            style={{
              width: `${((numCalendars + 2)/numCalendars*100)}%`,
              transform: `translateX(${translate}%)`,
              transition: `${transition}`
            }}
          >
            {calendars.map((calendar, i) => (
              <Box
                key={calendar.id}
                flex={1}
                px={1}
                style={{
                  height: getCalHeight(i, calHeight, numCalendars)
                }}
              >
                {calendar.calendar}
              </Box>
            ))}
          </Box>

          {/* calendar nav buttons */}
          <div className={classes.navButtons}>
            <IconButton
              size='small'
              onClick={() => navMonth(-1)}
            >
              <Icon type='nav-back' style={{position: 'relative', left: 5}} />
            </IconButton>
            <IconButton
              size='small'
              onClick={() => navMonth(+1)}
            >
              <Icon type='nav-forward' style={{position: 'relative', left: 1}} />
            </IconButton>
          </div>
        </div>
      </FeatureBox>
    </Fade>
  );
}
